import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';
import { rankingOf, type Placement, type RankingInfo, type ScoreCard } from './round-scores';

/**
 * A closed round's standings — the sheet pinned up at the end of the afternoon.
 *
 * **The server sends the places**, each class ranked by the competition's
 * regulation (`RankingStrategies`) — the same call the panel's live board makes,
 * so the two read the afternoon the same way. The page draws them and computes
 * none.
 *
 * Open to anybody with the link, which is why a row carries only what a results
 * sheet says: a name, a squad, and the cards behind the position.
 */
export interface RoundStandingEntry extends Placement {
  /** The entry. */
  id: string;
  /** Name and surname. Null when the desk never typed one. */
  full_name: string | null;
  /** The squad the athlete climbs for, as the sheet prints it. */
  team_name: string | null;
  /** Which class's table this row belongs in. */
  category_id: string | null;
  cards: ScoreCard[];
}

/** A class of the round: one table of its own. */
export interface StandingCategory {
  id: string;
  name: string;
}

/** One problem, the class that climbed it, and whether a judge stood at it. */
export interface StandingClimb {
  id: string;
  position: number;
  category_id: string | null;
  requires_judge?: boolean;
}

export interface RoundStandingsPage {
  entries: RoundStandingEntry[];
  categories: StandingCategory[];
  climbs: StandingClimb[];
  /** The regulation the places come from. */
  ranking: RankingInfo;
}

interface StandingsResponse {
  data: { id: string; type: 'round_standing'; attributes: RoundStandingEntry }[];
  meta?: {
    ranking_strategy?: string;
    ranking_judged_blocks?: boolean;
    categories?: StandingCategory[];
    round_climbs?: StandingClimb[];
    round_type?: string;
  };
}

class RoundStandingsService {
  /**
   * Every class of the round at once: the table somebody wants at the end is
   * their own class's, and they should not have to find it behind a tab.
   *
   * Served only once the round is closed (`round_not_closed` until then) — the
   * page asks when the round says it is.
   */
  async get(competitionSlug: string, roundId: string): Promise<RoundStandingsPage> {
    const response = await apiClient.get(
      `/api/competitions/${competitionSlug}/rounds/${roundId}/standings`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the standings');
    }

    const data: StandingsResponse = await response.json();
    return {
      entries: data.data.map((item) => item.attributes),
      categories: data.meta?.categories ?? [],
      climbs: data.meta?.round_climbs ?? [],
      ranking: rankingOf(data.meta),
    };
  }
}

export const roundStandingsService = new RoundStandingsService();
